/**
 * The buy panel: option chips in, everything else follows.
 *
 * The page already renders correctly for the selected variant before this
 * runs — prices, availability and the enquiry link are all server-side — so
 * this only has to keep them in step when the visitor picks a different size.
 * With JavaScript off, the <noscript> form reloads the page instead.
 *
 * Prices arrive pre-formatted from Liquid, so the shop's money format is never
 * reimplemented here.
 *
 * Announces `variant:change` on the document, which the sticky bar and gallery
 * listen for rather than reaching into this element.
 */
class ProductInfo extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-variant-data]');
    if (!data) return;

    try {
      this.variants = JSON.parse(data.textContent);
    } catch {
      return;
    }

    this.onChange = this.onChange.bind(this);
    this.addEventListener('change', this.onChange);
    this.updateOptionAvailability();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
  }

  onChange(event) {
    if (!event.target.matches('[data-option-position]')) return;

    const chosen = this.selectedOptions();
    const variant = this.variants.find(
      (candidate) =>
        candidate.options.length === chosen.length &&
        candidate.options.every((value, index) => value === chosen[index])
    );

    this.render(variant);
  }

  /**
   * The current option values, in option order.
   *
   * Pills and dropdowns are read the same way: a checked radio and a <select>
   * both report their choice as `.value`, so the picker's appearance never
   * reaches the rest of this file.
   */
  selectedOptions() {
    return [
      ...this.querySelectorAll(
        'select[data-option-position], input[type="radio"][data-option-position]:checked'
      ),
    ]
      .sort((a, b) => a.dataset.optionPosition - b.dataset.optionPosition)
      .map((node) => node.value);
  }

  render(variant) {
    this.updatePrice(variant);
    this.updateBuyButton(variant);
    this.updateOptionAvailability();
    this.updateUrl(variant);

    document.dispatchEvent(
      new CustomEvent('variant:change', { detail: { variant }, bubbles: true })
    );
  }

  /**
   * Marks sizes that are sold out given whatever else is selected.
   *
   * They stay selectable — someone who wants a King should be able to see it
   * is a King that is unavailable, not be quietly steered away from it. Pills
   * strike the label through; dropdown options say so in words, since a
   * <option> cannot be styled reliably across browsers.
   */
  updateOptionAvailability() {
    const selected = this.selectedOptions();

    for (const input of this.querySelectorAll('input[type="radio"][data-option-position]')) {
      const position = Number(input.dataset.optionPosition) - 1;
      const available = this.isReachable(position, input.value, selected);
      input.closest('.chip')?.classList.toggle('chip--soldout', !available);
    }

    for (const select of this.querySelectorAll('select[data-option-position]')) {
      const position = Number(select.dataset.optionPosition) - 1;
      for (const option of select.options) {
        // The first pass records the clean label, so later passes never
        // append a second "— Sold out" onto an already-marked one.
        option.dataset.label ??= option.textContent.trim();
        const available = this.isReachable(position, option.value, selected);
        option.textContent = available
          ? option.dataset.label
          : `${option.dataset.label} — ${select.dataset.soldOutLabel}`;
      }
    }
  }

  /** Is there an available variant with `value` at `position`, keeping the rest? */
  isReachable(position, value, selected) {
    const combination = [...selected];
    combination[position] = value;

    return this.variants.some(
      (variant) =>
        variant.available && variant.options.every((option, index) => option === combination[index])
    );
  }

  updatePrice(variant) {
    const price = this.querySelector('[data-price]');
    const compare = this.querySelector('[data-compare-price]');
    const save = this.querySelector('[data-save-tag]');

    // An option combination that does not exist keeps the last real price on
    // screen; the disabled button is what tells the visitor it is unavailable.
    if (price && variant) price.textContent = variant.price;

    const onSale = Boolean(variant && variant.compare_at_price);
    if (compare) {
      compare.textContent = onSale ? variant.compare_at_price : '';
      compare.toggleAttribute('hidden', !onSale);
    }
    if (save) save.toggleAttribute('hidden', !onSale);
  }

  updateBuyButton(variant) {
    const idInput = this.querySelector('[data-variant-id]');
    if (idInput && variant) idInput.value = variant.id;

    // A quantity beside a disabled button is a control that lies, so it goes
    // wherever the button does.
    const quantity = this.querySelector('[data-quantity]');
    if (quantity) quantity.hidden = !(variant && variant.available);

    const button = this.querySelector('[data-buy-button]');
    if (button) {
      const available = Boolean(variant && variant.available);
      button.disabled = !available;
      const label = button.querySelector('[data-buy-label]');
      if (label) {
        label.textContent = available
          ? button.dataset.labelAdd || label.textContent
          : button.dataset.labelSoldOut || label.textContent;
      }
    }

    const inquire = this.querySelector('[data-inquire-button]');
    if (inquire && variant) inquire.href = this.whatsappHref(inquire, variant);
  }

  whatsappHref(link, variant) {
    const { waNumber, waTemplate, waProduct, waUrl } = link.dataset;
    const message = waTemplate
      .replace('[product]', waProduct)
      .replace('[variant]', variant.wa_variant)
      .replace('[price]', variant.price)
      .replace('[url]', `${waUrl}?variant=${variant.id}`);

    return `https://wa.me/${waNumber}?text=${encodeURIComponent(message)}`;
  }

  /** Keeps the address bar shareable without adding a history entry per chip. */
  updateUrl(variant) {
    if (!variant) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url);
  }
}

customElements.define('product-info', ProductInfo);
